import React, { useCallback, useRef } from 'react';

import { FormattedMessage } from 'react-intl';

import { useFlags } from '@atlaskit/flag/use-flags';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import type { UploadsStartEventPayload } from '@atlaskit/media-picker/types';
import { token } from '@atlaskit/tokens';

import {
	FileStatus,
	type FilesValue,
} from '../../../../../../../../../../common/types/form/form-fields/field-values';
import {
	useAttachmentConfiguration,
	useFormRendererActions,
	useTotalFormAttachments,
} from '../../../../../../../../../../controllers/form-renderer-store';
import { messages } from './messages';

const DEFAULT_ATTACHMENTS_LIMIT = 50;

export const useOnUploadStart: (
	currentFilesValue: FilesValue,
	fieldId: string,
	collectionName?: string,
) => (payload: UploadsStartEventPayload) => void = (
	currentFilesValue: FilesValue,
	fieldId: string,
	collectionName?: string,
) => {
	const attachmentConfiguration = useAttachmentConfiguration();
	const totalFormAttachments = useTotalFormAttachments();
	const { showFlag } = useFlags();
	const { updateModifiedValue } = useFormRendererActions();

	// Dynamic state that needs to be accessed from onUploadStart need to be stored in a ref.
	// This is due to how the Media components work. The callbacks are cached, so passing
	// in a new onUploadStart callback into Media components will not register the
	// updated callback.
	const currentFilesValueRef = useRef<FilesValue>(currentFilesValue);
	currentFilesValueRef.current = currentFilesValue;

	const totalFormAttachmentsRef = useRef<number>(totalFormAttachments);
	totalFormAttachmentsRef.current = totalFormAttachments;

	const onUploadStart = useCallback(
		(payload: UploadsStartEventPayload) => {
			const filesValue = currentFilesValueRef.current;
			const attachmentLimit =
				attachmentConfiguration?.upload?.totalAttachmentsNumberLimit ?? DEFAULT_ATTACHMENTS_LIMIT;
			const newTotalFormAttachments = totalFormAttachmentsRef.current + payload.files.length;

			if (newTotalFormAttachments > attachmentLimit) {
				showFlag({
					icon: (
						<StatusErrorIcon spacing="spacious" label="" color={token('color.icon.accent.red')} />
					),
					title: <FormattedMessage {...messages.uploadErrorTitle} />,
					description: <FormattedMessage {...messages.uploadErrorAttachmentLimitExceeded} />,
				});
				return;
			}

			const updatedFiles = filesValue.files.concat(
				payload.files.map((mediaFile) => ({
					creationDate: mediaFile.creationDate,
					identifier: {
						mediaItemType: 'file',
						id: mediaFile.id,
						occurrenceKey: mediaFile.occurrenceKey,
						...(collectionName ? { collectionName } : {}),
					},
					name: mediaFile.name,
					size: mediaFile.size,
					type: mediaFile.type,
				})),
			);
			updateModifiedValue(fieldId, {
				files: updatedFiles,
				fileStatus: FileStatus.UPLOADING,
			});
		},
		[
			attachmentConfiguration,
			collectionName,
			currentFilesValueRef,
			fieldId,
			showFlag,
			totalFormAttachmentsRef,
			updateModifiedValue,
		],
	);

	return onUploadStart;
};
